import { cartLogoBoxSize, DEFAULT_LOGO_SIZE } from './widget-logo';

/** The built-in mark at checkout's default size, so a brand-new cart widget already matches it. */
const OP_MARK_DEFAULT_BOX = `${cartLogoBoxSize(DEFAULT_LOGO_SIZE, true)}px`;

/**
 * Distance from the mark to the title. The retired PNG spent half of it on transparent padding
 * inside its own box, so the mark now draws at its real size and the stack holds all of the gap.
 */
const OP_MARK_TITLE_GAP = '16px';

/** The description slot's shipped text; the compiler finds the node by it. */
export const DEFAULT_WIDGET_DESCRIPTION = 'Protection for Damage, Loss, Theft & More!';

/** Space under the title, in px. Shipped tight because the customizer's Title Spacing edits it. */
export const DEFAULT_TITLE_SPACING = 0;

export const WIDGET_CONFIGS = {
  ToggleCart: {
    name: '',
    description: 'Toggle Cart template',
    type: 'CART',
    config: [
      {
        type: 'vertical-stack',
        attributes: {
          gap: '0',
          padding: '0',
          margin: '0 0 1em 0',
          'align-items': 'stretch',
          'justify-content': 'flex-start',
        },
        children: [
          {
            type: 'bordered-container',
            attributes: {
              padding: '2px',
              'border-width': '1px 0 1px 0',
              'border-style': 'solid',
              'border-color': '#e6e6e6',
              'background-color': 'transparent',
              'border-radius': '0',
            },
            children: [
              {
                type: 'horizontal-stack',
                attributes: {
                  padding: '8px',
                  'align-items': 'flex-start',
                  'justify-content': 'space-between',
                  gap: '0',
                },
                children: [
                  {
                    type: 'horizontal-stack',
                    attributes: {
                      gap: OP_MARK_TITLE_GAP,
                      'align-items': 'center',
                      'justify-content': 'flex-start',
                      padding: '0',
                      width: 'auto',
                    },
                    children: [
                      {
                        type: 'ui-image',
                        attributes: {
                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/OP-CE-Logo.svg',
                          alt: 'Checkout+',
                          width: OP_MARK_DEFAULT_BOX,
                          height: OP_MARK_DEFAULT_BOX,
                          role: 'img',
                          loading: 'eager',
                        },
                      },
                      {
                        type: 'vertical-stack',
                        attributes: {
                          gap: `${DEFAULT_TITLE_SPACING}px`,
                          padding: '0px',
                          height: '100%',
                          'align-items': 'flex-start',
                          'justify-content': 'flex-start',
                          width: 'auto',
                        },
                        children: [
                          {
                            type: 'horizontal-stack',
                            attributes: {
                              gap: '8px',
                              padding: '0px',
                              'align-items': 'center',
                              'justify-content': 'flex-start',
                              width: 'auto',
                            },
                            children: [
                              {
                                type: 'custom-text',
                                attributes: {
                                  text: 'Order Protection',
                                  font: 'Inter',
                                  size: '15px',
                                  color: '#1D2433',
                                  weight: '600',
                                  align: 'left',
                                  // Matches the description, so Title Spacing is the whole gap.
                                  'line-height': '1em',
                                  'text-transform': 'none',
                                  'letter-spacing': 'normal',
                                },
                              },
                              {
                                type: 'ui-modal',
                                attributes: {
                                  'aria-label': 'Information Modal',
                                  role: 'dialog',
                                },
                                children: [
                                  {
                                    type: 'ui-image',
                                    attributes: {
                                      src: 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg',
                                      alt: 'Info Modal',
                                      width: '12px',
                                      height: '12px',
                                      role: 'button',
                                      'aria-label': 'Order Protection Info Modal',
                                      'aria-haspopup': 'dialog',
                                      tabIndex: '0',
                                      isInfoModal: true,
                                      loading: 'eager',
                                    },
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            type: 'custom-text',
                            attributes: {
                              text: DEFAULT_WIDGET_DESCRIPTION,
                              font: 'Inter',
                              size: '12px',
                              color: 'rgba(29, 36, 51, 0.8)',
                              weight: '400',
                              align: 'left',
                              'line-height': '1em',
                              'text-transform': 'none',
                              'letter-spacing': 'normal',
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    type: 'vertical-stack',
                    attributes: {
                      gap: '0',
                      padding: '0px',
                      width: 'fit-content',
                      height: '100%',
                      'align-items': 'flex-end',
                      'justify-content': 'flex-start',
                    },
                    children: [
                      {
                        type: 'horizontal-stack',
                        attributes: {
                          gap: '8px',
                          'align-items': 'center',
                          'justify-content': 'flex-end',
                          padding: '0',
                          width: 'fit-content',
                        },
                        children: [
                          {
                            type: 'ui-price',
                            attributes: {
                              currency: 'USD',
                              value: '1000',
                              font: 'Inter',
                              size: '12px',
                              color: '#1D2433',
                              weight: '600',
                              type: 'variant',
                              align: 'right',
                              'line-height': 'normal',
                              'letter-spacing': 'normal',
                              width: 'fit-content',
                              'min-width': '60px',
                              'text-align': 'right',
                            },
                            listens: ['widget/loaded', 'cart/updated'],
                          },
                          {
                            type: 'ui-switch',
                            attributes: {
                              'aria-label': 'Toggle Checkout+ protection',
                              role: 'switch',
                              tabIndex: '0',
                            },
                            listens: ['widget/loaded', 'cart/updated'],
                            emits: ['protection/toggled'],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  ComparisonCart: {
    name: 'Comparison Cart',
    description: 'Comparison Cart template',
    type: 'CART',
    config: [
      {
        type: 'bordered-container',
        attributes: {
          padding: '16px',
          'border-radius': '6px',
          'background-color': '#FFF',
        },
        children: [
          {
            type: 'vertical-stack',
            children: [
              {
                type: 'horizontal-stack',
                children: [
                  {
                    type: 'custom-text',
                    attributes: {
                      font: 'Inter',
                      size: '14px',
                      text: 'Choose your Coverage',
                      color: 'black',
                      weight: 'bold',
                    },
                  },
                  {
                    type: 'ui-modal',
                    children: [
                      {
                        type: 'ui-image',
                        attributes: {
                          alt: 'Info Modal',
                          src: 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg',
                          role: 'button',
                          width: '12px',
                          height: '12px',
                          loading: 'eager',
                          tabIndex: '0',
                          'aria-label': 'Order Protection Info Modal',
                          isInfoModal: true,
                          'aria-haspopup': 'dialog',
                        },
                      },
                    ],
                    attributes: {
                      role: 'dialog',
                      'aria-label': 'Information Modal',
                    },
                  },
                ],
                attributes: {
                  gap: '8px',
                  width: 'auto',
                  padding: '0px',
                  'align-items': 'center',
                  'justify-content': 'flex-start',
                },
              },
              {
                type: 'horizontal-stack',
                attributes: {
                  gap: '16px',
                  'justify-content': 'stretch',
                  padding: '16px 0',
                  width: '100%',
                  display: 'flex',
                  'align-items': 'stretch',
                },
                children: [
                  {
                    type: 'radio-group',
                    listens: [
                      'user-preference/loaded',
                      'settings/loaded',
                      'coverage/selection-updated',
                      'coverage/selected',
                    ],
                    emits: ['coverage/selected'],
                    children: [
                      {
                        type: 'radio-option',
                        attributes: {
                          id: 'VIP',
                          width: '100%',
                          flex: '1 1 auto',
                        },
                        children: [
                          {
                            type: 'bordered-container',
                            children: [
                              {
                                type: 'horizontal-stack',
                                children: [
                                  {
                                    type: 'custom-text',
                                    attributes: {
                                      font: 'Inter',
                                      size: '12px',
                                      text: 'VIP',
                                      color: 'black',
                                      weight: 'bold',
                                    },
                                  },
                                  {
                                    type: 'ui-price',
                                    attributes: {
                                      font: 'Inter',
                                      size: '12px',
                                      color: 'gray',
                                      value: '635',
                                      currency: 'USD',
                                      type: 'variant',
                                    },
                                    listens: ['widget/loaded', 'cart/updated'],
                                  },
                                ],
                                attributes: {
                                  gap: '8px',
                                  'align-items': 'center',
                                  'justify-content': 'space-between',
                                  width: '100%',
                                },
                              },
                              {
                                type: 'list-group',
                                attributes: {
                                  gap: '4px',
                                  padding: '8px 0px',
                                  alignItems: 'flex-start',
                                },
                                children: [
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 1,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Check.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.8',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Defective Items',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 2,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Check.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.8',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Theft, Lost, Damaged',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 3,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Check.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.8',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Priority 24/7 Support',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                ],
                              },
                            ],
                            attributes: {
                              padding: '12px 16px',
                              'border-radius': '6px',
                              'background-color': '#FFF',
                              display: 'flex',
                              'flex-direction': 'column',
                              'align-items': 'flex-start',
                              'selected-border-color': '#5B1FD3',
                              gap: '8px',
                              flex: '1 1 auto',
                              'min-width': '0',
                              width: '100%',
                              cursor: 'pointer',
                            },
                          },
                        ],
                      },
                      {
                        type: 'radio-option',
                        attributes: {
                          id: 'Standard',
                          width: '100%',
                          flex: '1 1 auto',
                        },
                        children: [
                          {
                            type: 'bordered-container',
                            children: [
                              {
                                type: 'horizontal-stack',
                                children: [
                                  {
                                    type: 'custom-text',
                                    attributes: {
                                      font: 'Inter',
                                      size: '12px',
                                      text: 'Standard',
                                      color: 'black',
                                      weight: 'bold',
                                    },
                                  },
                                  {
                                    type: 'ui-price',
                                    attributes: {
                                      font: 'Inter',
                                      size: '12px',
                                      color: 'gray',
                                      value: '0.01',
                                      currency: 'USD',
                                    },
                                  },
                                ],
                                attributes: {
                                  gap: '8px',
                                  'align-items': 'center',
                                  'justify-content': 'space-between',
                                  width: '100%',
                                },
                              },
                              {
                                type: 'list-group',
                                attributes: {
                                  gap: '4px',
                                  padding: '8px 0px',
                                  alignItems: 'flex-start',
                                },
                                children: [
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 1,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Check.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.7',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Defective Items',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 2,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Cancel.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.7',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Theft, Lost, Damaged',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'list-item',
                                    attributes: {
                                      order: 3,
                                      gap: '8px',
                                      alignItems: 'center',
                                    },
                                    children: [
                                      {
                                        type: 'ui-image',
                                        attributes: {
                                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Cancel.svg',
                                          width: '12px',
                                          height: '12px',
                                          opacity: '0.7',
                                        },
                                      },
                                      {
                                        type: 'custom-text',
                                        attributes: {
                                          font: 'Geist',
                                          size: '11px',
                                          text: 'Priority 24/7 Support',
                                          color: 'rgba(29, 36, 51, 0.65)',
                                          weight: '400',
                                          'line-height': '12px',
                                        },
                                      },
                                    ],
                                  },
                                ],
                              },
                            ],
                            attributes: {
                              padding: '12px 16px',
                              'border-radius': '6px',
                              'background-color': '#FFF',
                              display: 'flex',
                              'flex-direction': 'column',
                              'align-items': 'flex-start',
                              gap: '8px',
                              flex: '1 1 auto',
                              'min-width': '0',
                              width: '100%',
                              cursor: 'pointer',
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'custom-text',
                attributes: {
                  font: 'Inter',
                  size: '12px',
                  text: 'By selecting VIP, I agree to include shipping protection and priority support to my order at checkout.',
                  color: 'gray',
                },
              },
            ],
            attributes: {
              padding: '0px',
              'align-items': 'flex-start',
              width: '100%',
            },
          },
        ],
      },
    ],
  },
  CoverageCart: {
    name: 'Coverage Options',
    description: 'No Coverage / Basic / extended warranty tiers as compact chips',
    type: 'CART',
    config: [
      {
        type: 'vertical-stack',
        attributes: {
          gap: '8px',
          padding: '0',
          margin: '0 0 1em 0',
          'align-items': 'stretch',
          'justify-content': 'flex-start',
        },
        children: [
          {
            type: 'horizontal-stack',
            attributes: {
              padding: '0',
              'align-items': 'center',
              'justify-content': 'space-between',
              gap: '8px',
            },
            children: [
              {
                type: 'custom-text',
                attributes: {
                  text: 'Protect your order',
                  font: 'inherit',
                  size: '13px',
                  weight: '600',
                  color: '#1D2433',
                },
              },
              {
                type: 'ui-modal',
                attributes: {},
                children: [
                  {
                    type: 'ui-image',
                    attributes: {
                      src: 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg',
                      width: '14px',
                      height: '14px',
                      isInfoModal: true,
                    },
                  },
                ],
              },
            ],
          },
          {
            type: 'ew-coverage',
            children: [
              {
                type: 'ew-coverage-option',
                attributes: {
                  tier: 'none',
                  title: 'No Coverage',
                  'price-label': 'Free',
                  features: 'No Resolutions for Shipping Issues\nNo Free Returns',
                },
              },
              {
                type: 'ew-coverage-option',
                attributes: {
                  tier: 'shipping',
                  title: 'Basic',
                  features: '24/7 Support\nResolutions for Shipping Issues\n45 Day Returns',
                },
              },
              {
                type: 'ew-coverage-option',
                attributes: {
                  tier: 'warranty',
                  title: '{{duration}} Year',
                  features: 'Everything in Basic Coverage\n{{duration}} Year Extended Warranty',
                },
              },
            ],
            attributes: {
              disclaimer: 'Includes shipping protection, priority support & an extended warranty.',
              'terms-label': 'Terms',
              terms:
                'By leaving Extended Coverage selected, I agree to include shipping protection, priority support, and an extended warranty to my order at checkout.',
              'show-none': 'true',
              'accent-color': '#5B1FD3',
              'selected-background': '#F3EEFE',
              'border-color': '#C9D0DD',
              'chip-radius': '8px',
            },
          },
        ],
      },
    ],
  },
  // Mirrors cart-widget's INCLUDED_TEMPLATE; no ui-switch or price, or the storefront falls back to its default.
  IncludedCart: {
    name: 'Included in shipping',
    description: 'Shows that protection comes with shipping. No toggle, no price.',
    type: 'CART',
    config: [
      {
        type: 'included-badge',
        attributes: {
          text: 'Order Protection included with shipping',
          'show-icon': true,
          color: '#1F1F1F',
          background: '#F4F4F4',
          font: 'inherit',
          size: '13px',
        },
        children: [
          {
            type: 'ui-modal',
            attributes: { modalStep1Title: 'Order Protection is included with your shipping.' },
            children: [
              {
                type: 'ui-image',
                attributes: {
                  src: 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg',
                  alt: 'Info Modal',
                  width: '14px',
                  height: '14px',
                  role: 'button',
                  'aria-label': 'Order Protection Info Modal',
                  'aria-haspopup': 'dialog',
                  tabIndex: '0',
                  isInfoModal: true,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  CheckoutPlus: {
    name: 'Checkout+',
    description: 'Checkout+ template',
    type: 'CART',
    config: [
      {
        type: 'vertical-stack',
        attributes: {
          gap: '0',
          padding: '0',
          margin: '0 0 1em 0',
          'align-items': 'stretch',
          'justify-content': 'flex-start',
        },
        children: [
          {
            type: 'bordered-container',
            attributes: {
              padding: '2px',
              'border-width': '1px 0 1px 0',
              'border-style': 'solid',
              'border-color': '#e6e6e6',
              'background-color': 'transparent',
              'border-radius': '0',
            },
            children: [
              {
                type: 'horizontal-stack',
                attributes: {
                  padding: '8px',
                  'align-items': 'flex-start',
                  'justify-content': 'space-between',
                  gap: '0',
                },
                children: [
                  {
                    type: 'horizontal-stack',
                    attributes: {
                      gap: OP_MARK_TITLE_GAP,
                      'align-items': 'center',
                      'justify-content': 'flex-start',
                      padding: '0',
                      width: 'auto',
                    },
                    children: [
                      {
                        type: 'ui-image',
                        attributes: {
                          src: 'https://storage.googleapis.com/cdn.orderprotection.com/Images/OP-CE-Logo.svg',
                          alt: 'Checkout+',
                          width: OP_MARK_DEFAULT_BOX,
                          height: OP_MARK_DEFAULT_BOX,
                          role: 'img',
                          loading: 'eager',
                        },
                      },
                      {
                        type: 'vertical-stack',
                        attributes: {
                          gap: `${DEFAULT_TITLE_SPACING}px`,
                          padding: '0px',
                          height: '100%',
                          'align-items': 'flex-start',
                          'justify-content': 'flex-start',
                          width: 'auto',
                        },
                        children: [
                          {
                            type: 'horizontal-stack',
                            attributes: {
                              gap: '8px',
                              padding: '0px',
                              'align-items': 'center',
                              'justify-content': 'flex-start',
                              width: 'auto',
                            },
                            children: [
                              {
                                type: 'custom-text',
                                attributes: {
                                  text: 'Checkout+',
                                  font: 'Inter',
                                  size: '15px',
                                  color: '#1D2433',
                                  weight: '600',
                                  align: 'left',
                                  // Matches the description, so Title Spacing is the whole gap.
                                  'line-height': '1em',
                                  'text-transform': 'none',
                                  'letter-spacing': 'normal',
                                },
                              },
                              {
                                type: 'ui-modal',
                                attributes: {
                                  'aria-label': 'Information Modal',
                                  role: 'dialog',
                                },
                                children: [
                                  {
                                    type: 'ui-image',
                                    attributes: {
                                      src: 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg',
                                      alt: 'Info Modal',
                                      width: '12px',
                                      height: '12px',
                                      role: 'button',
                                      'aria-label': 'Order Protection Info Modal',
                                      'aria-haspopup': 'dialog',
                                      tabIndex: '0',
                                      isInfoModal: true,
                                      loading: 'eager',
                                    },
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            type: 'custom-text',
                            attributes: {
                              text: DEFAULT_WIDGET_DESCRIPTION,
                              font: 'Inter',
                              size: '12px',
                              color: 'rgba(29, 36, 51, 0.8)',
                              weight: '400',
                              align: 'left',
                              'line-height': '1em',
                              'text-transform': 'none',
                              'letter-spacing': 'normal',
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    type: 'vertical-stack',
                    attributes: {
                      gap: '0',
                      padding: '0px',
                      width: 'fit-content',
                      height: '100%',
                      'align-items': 'flex-end',
                      'justify-content': 'flex-start',
                    },
                    children: [
                      {
                        type: 'horizontal-stack',
                        attributes: {
                          gap: '8px',
                          'align-items': 'center',
                          'justify-content': 'flex-end',
                          padding: '0',
                          width: 'fit-content',
                        },
                        children: [
                          {
                            type: 'ui-price',
                            attributes: {
                              value: 'Loading...',
                              currency: 'USD',
                              font: 'Inter',
                              size: '12px',
                              color: '#1D2433',
                              weight: '600',
                              type: 'variant',
                              align: 'right',
                              'line-height': 'normal',
                              'letter-spacing': 'normal',
                              width: 'fit-content',
                              'min-width': '60px',
                              'text-align': 'right',
                            },
                            listens: ['widget/loaded', 'cart/updated'],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            type: 'horizontal-stack',
            attributes: {
              gap: '6px',
              padding: '8px',
              'align-items': 'center',
              'justify-content': 'space-between',
            },
            children: [
              {
                type: 'custom-text',
                attributes: {
                  text: 'Subtotal',
                  font: 'Inter',
                  size: '13px',
                  color: 'rgba(29, 36, 51, 0.8)',
                  weight: '600',
                  align: 'left',
                  'line-height': 'normal',
                  'text-transform': 'none',
                  'letter-spacing': 'normal',
                },
              },
              {
                type: 'ui-price',
                attributes: {
                  value: '216.00',
                  currency: 'USD',
                  font: 'Inter',
                  size: '13px',
                  color: 'rgba(29, 36, 51, 0.8)',
                  weight: '600',
                  type: 'subtotal',
                  align: 'right',
                  'line-height': 'normal',
                  'letter-spacing': 'normal',
                  width: 'fit-content',
                  'min-width': '60px',
                  'text-align': 'right',
                },
                listens: ['widget/loaded', 'cart/updated'],
              },
            ],
          },
          {
            type: 'ui-button',
            attributes: {
              text: 'Checkout+',
              'background-color': '#000000',
              color: '#ffffff',
              width: '100%',
              padding: '12px 16px',
              'border-radius': '4px',
              'font-size': '14px',
              'text-transform': 'uppercase',
              'text-weight': '600',
              'letter-spacing': '0.05em',
              border: 'none',
              cursor: 'pointer',
              'font-family': 'Inter',
              'align-items': 'center',
              'justify-content': 'center',
            },
            emits: ['protection-add'],
          },
          {
            type: 'horizontal-stack',
            attributes: {
              gap: '0',
              padding: '8px',
              'align-items': 'center',
              'justify-content': 'center',
            },
            children: [
              {
                type: 'custom-text',
                attributes: {
                  text: 'Continue without Checkout+',
                  font: 'Inter',
                  size: '12px',
                  color: 'rgba(29, 36, 51, 0.65)',
                  href: '#',
                  weight: 'normal',
                  align: 'center',
                  'line-height': 'normal',
                  'text-transform': 'none',
                  'letter-spacing': 'normal',
                },
                emits: ['protection-remove'],
              },
            ],
          },
        ],
      },
    ],
  },

  ToggleCheckout: {
    name: 'Toggle Checkout',
    description: 'Toggle Checkout template',
    type: 'CHECKOUT',
    config: {
      isV2: true,
      onlyCE: false,
      excludeIntlOrders: false,
      excludeUKIP: false,
      allowedIntlCountries: '',
      titleText: '',
      titleTextSize: 'base',
      titleTextBold: false,
      infoText: '',
      infoTextSize: 'small',
      showDisclaimerText: false,
      disclaimerTextOn:
        'By leaving the toggle selected, I agree to include shipping insurance for added protection and priority support. I understand this will be added to my order at checkout.',
      disclaimerTextOnSize: '',
      disclaimerTextOff: '',
      disclaimerTextOffSize: '',
      actionButtonType: '',
      showBorder: false,
      brandFundedText: 'Free',
      showBrandFundedWidget: true,
      hiddenShippingMethods: '',
      suppressWhenSellingPlanInCart: false,
      suppressedPaymentHandles: '',
      paymentMethodRemovalText:
        'Order Protection was removed because it is not available with the selected payment method.',
      customDisclaimerText: '',
      displayType: 'textBlock',
      bannerStatus: 'info',
      customDisclaimerTextSize: 'small',
      customDisclaimerTextColor: 'base',
      customDisclaimerTextBold: false,
      customTextBlockBackground: 'transparent',
      customTextBlockBorder: false,
      customTextBlockBorderWidth: 'thin',
      customTextBlockBorderRadius: 'small',
      customTextBlockBorderColor: 'base',
      borderWidth: 'thin',
      showInfoModal: true,
      debugMode: false,
      storeCreditText: '& receive a free {{store_credit_value}} gift card!',
    },
  },
};
